import { Activity, ActivityType } from '@/types'
import { 
  PlusIcon,
  PencilIcon,
  ArrowPathIcon,
  UserPlusIcon,
  ChatBubbleLeftIcon,
  ClockIcon
} from '@heroicons/react/24/outline'

interface ActivityLogProps {
  activities: Activity[]
  loading?: boolean
}

const activityConfig = {
  [ActivityType.CREATED]: {
    icon: PlusIcon,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    label: '建立了議題'
  },
  [ActivityType.UPDATED]: {
    icon: PencilIcon,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    label: '更新了議題'
  },
  [ActivityType.STATUS_CHANGED]: {
    icon: ArrowPathIcon,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    label: '變更了狀態'
  },
  [ActivityType.ASSIGNED]: {
    icon: UserPlusIcon,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    label: '指派了負責人'
  },
  [ActivityType.COMMENTED]: {
    icon: ChatBubbleLeftIcon,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    label: '新增了留言'
  }
}

// 取得欄位標籤
const getFieldLabel = (field: string) => {
  const fieldLabels: Record<string, string> = {
    'title': '標題',
    'description': '描述',
    'status': '狀態',
    'customStatus': '自訂狀態',
    'priority': '優先級',
    'assigneeId': '負責人',
    'comment': '留言'
  }
  return fieldLabels[field] || field
}

const ActivityLog = ({ activities, loading = false }: ActivityLogProps) => {
  if (loading) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">活動記錄</h3>
        <div className="animate-pulse space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex space-x-3">
              <div className="w-8 h-8 bg-gray-200 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h3 className="text-lg font-medium text-gray-900">
          活動記錄 ({activities.length})
        </h3>
      </div>

      {activities.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <ClockIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>尚無活動記錄</p>
        </div>
      ) : (
        <div className="flow-root">
          <ul className="-mb-8">
            {activities.map((activity, activityIndex) => {
              const config = activityConfig[activity.type as keyof typeof activityConfig]
              const Icon = config?.icon
              
              return (
                <li key={activity.id}>
                  <div className="relative pb-8">
                    {activityIndex !== activities.length - 1 ? (
                      <span
                        className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                        aria-hidden="true"
                      />
                    ) : null}
                    
                    <div className="relative flex space-x-3">
                      {/* 活動圖示 */}
                      <div>
                        <span className={`h-8 w-8 rounded-full ${config?.bgColor || 'bg-gray-50'} flex items-center justify-center ring-8 ring-white`}>
                          {Icon && <Icon className={`h-4 w-4 ${config?.color || 'text-gray-600'}`} aria-hidden="true" />}
                        </span>
                      </div>
                      
                      {/* 活動內容 */}
                      <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                        <div>
                          <div className="flex items-center space-x-2">
                            {/* 使用者頭像 */}
                            {activity.user?.avatar ? (
                              <img
                                src={activity.user.avatar}
                                alt={activity.user?.name || 'User'}
                                className="w-6 h-6 rounded-full"
                              />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
                                <span className="text-xs font-medium text-gray-600">
                                  {activity.user?.name?.charAt(0).toUpperCase() || 'U'}
                                </span>
                              </div>
                            )}
                            
                            {/* 活動描述 */}
                            <div className="text-sm text-gray-700">
                              <span className="font-medium text-gray-900">
                                {activity.user?.name || 'Unknown User'}
                              </span>
                              {' '}
                              <span className="text-gray-600">
                                {config?.label || 'performed an action'}
                              </span>
                              {activity.description && (
                                <span className="text-gray-500">
                                  {' · '}{activity.description}
                                </span>
                              )}
                            </div>
                            
                            {/* 變更詳細內容 */}
                            {activity.field && (activity.oldValue !== undefined || activity.newValue !== undefined) && (
                              <div className="mt-2 text-sm">
                                <div className="flex items-center mb-1">
                                  <span className="text-xs text-gray-500 font-medium">變更詳情：</span>
                                  <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                                    {getFieldLabel(activity.field)}
                                  </span>
                                </div>
                                
                                {/* 新增留言的情況 */}
                                {activity.type === 'COMMENTED' && activity.newValue && !activity.oldValue ? (
                                  <div className="bg-green-50 rounded-md p-2">
                                    <div className="text-xs text-gray-500 mb-1">留言內容：</div>
                                    <div className="text-xs text-gray-700 break-words">
                                      {activity.newValue}
                                    </div>
                                  </div>
                                ) : (
                                  /* 修改或刪除的情況 */
                                  <div className="bg-gray-50 rounded-md p-2 space-y-1">
                                    {activity.oldValue !== undefined && (
                                      <div className="flex items-start space-x-2">
                                        <span className="text-xs text-red-600 font-medium min-w-0 flex-shrink-0">變更前：</span>
                                        <span className="text-xs text-gray-700 break-words">
                                          {activity.oldValue || '(空值)'}
                                        </span>
                                      </div>
                                    )}
                                    {activity.newValue !== undefined && (
                                      <div className="flex items-start space-x-2">
                                        <span className="text-xs text-green-600 font-medium min-w-0 flex-shrink-0">變更後：</span>
                                        <span className="text-xs text-gray-700 break-words">
                                          {activity.newValue || '(空值)'}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* 時間戳記 */}
                        <div className="whitespace-nowrap text-right text-sm text-gray-500 flex items-center">
                          <time dateTime={new Date(activity.createdAt).toISOString()}>
                            {formatActivityTime(activity.createdAt)}
                          </time>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

// 格式化活動時間
const formatActivityTime = (timestamp: string | Date) => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  
  if (diffInSeconds < 60) {
    return '剛剛'
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60)
    return `${minutes} 分鐘前`
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600)
    return `${hours} 小時前`
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400)
    return `${days} 天前`
  } else {
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }
}

export default ActivityLog